import { useState } from 'react';

import { css } from '@emotion/react';
import { FallbackProps } from 'react-error-boundary';
import { useQueryClient } from 'react-query';
import { useSetRecoilState } from 'recoil';

import Button from '@components/Common/Button';

import {
  accessTokenStorage,
  expirationDateStorage,
} from '@storage/authStorage';

import { useInternalRouter } from '@hooks/useInternalRouter';

import { palette } from '@styles/theme';

import { auth } from '@constants/state';

import { getErrorMessage } from '@apis/api';

const ContentsErrorFallback = ({
  error,
  resetErrorBoundary,
}: FallbackProps) => {
  const queryClient = useQueryClient();
  const router = useInternalRouter();
  const { status } = error.response;
  const isNotAuthorized = status === 401 || status === 403;
  const setAuth = useSetRecoilState(auth);
  const [open, setOpen] = useState<boolean>(true);
  const { title, content } = getErrorMessage(status);

  const handleOnClick = () => {
    setAuth({
      isLogin: false,
      name: '',
      email: '',
      role: undefined,
      company: '',
      companyId: undefined,
    });
    accessTokenStorage.clear();
    expirationDateStorage.clear();
    localStorage.removeItem('authentication');
    setOpen(false);
    queryClient.clear();
    router.replace('/login');
  };

  return isNotAuthorized ? (
    <div css={[wrapper, open && openModal]}>
      <div css={modal}>
        <div css={header}>로그인 세션이 만료되었습니다.</div>
        <div css={contents}>
          <div>
            로그인 세션이 만료 되었습니다.
            <br />
            다시 로그인해 주세요.
          </div>
          <div css={buttonWrap}>
            <Button
              label="로그인 하러가기"
              theme="secondary"
              handleClick={handleOnClick}
            />
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div css={container}>
      <p css={[text, titles]}>{title}</p>
      <p css={text}>{content}</p>
      <Button
        label={'다시 시도'}
        theme="secondary"
        handleClick={resetErrorBoundary}
      />
    </div>
  );
};

export default ContentsErrorFallback;

const wrapper = css`
  display: none;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.4);

  @keyframes modal-bg-show {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const openModal = css`
  display: flex;
  align-items: center;
  justify-content: center;
  animation: modal-bg-show 0.3s;
`;

const modal = css`
  position: relative;
  width: 360px;
  height: auto;
  background-color: #ffffff;
  box-shadow: 0px 5px 16px rgba(0, 0, 0, 0.08);
  border-radius: 10px;
`;

const header = css`
  display: flex;
  position: relative;
  min-height: 72px;
  max-height: 94px;
  justify-content: flex-start;
  padding-left: 20px;
  align-items: center;
  font-size: 20px;
  font-weight: 700;
  color: #222;
  border-bottom: 1px solid ${palette.gray3};
`;

const contents = css`
  display: flex;
  flex-direction: column;
  padding: 30px;
`;

const buttonWrap = css`
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
`;

const container = css`
  width: 100%;
  height: calc(100vh - 60px);
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  justify-content: center;
  button {
    background-color: ${palette.primaryColor};
    &:hover,
    &:active {
      background-color: ${palette.primaryColor};
      opacity: 0.8;
    }
  }
`;

const text = css`
  font-size: 14px;
`;

const titles = css`
  font-size: 20px;
  font-weight: 700;
`;
